// Critter Crossing — pure game logic.
//
// An early-math river game ported from the Pixley iOS SwiftUI version.
// Woodland critters walk to the near bank and ask to cross; each crossing is
// one math challenge played on the water. Solving it raises stepping stones,
// then the critter hops across to the far bank. Five crossings make a round.
//
// This module owns every piece of stateless logic: the unit-space layout
// table, the critter roster, the six challenge templates, the round recipe,
// and the scoring. It has no React or DOM dependency so it can be unit-tested.

const ASSET_BASE = "/assets/games/critter-crossing";
export const asset = (name) => `${ASSET_BASE}/${name}.png`;

// ---------------------------------------------------------------------------
// Number words + tiny helpers
// ---------------------------------------------------------------------------

const NUMBER_WORDS = ["zero", "one", "two", "three", "four", "five", "six", "seven", "eight"];

export const numberWord = (n) => (NUMBER_WORDS[n] !== undefined ? NUMBER_WORDS[n] : String(n));
const cap = (word) => (word ? word[0].toUpperCase() + word.slice(1) : word);

const randomInt = (min, max, random = Math.random) =>
  Math.floor(random() * (max - min + 1)) + min;

export const shuffle = (items, random = Math.random) => {
  const result = [...items];
  for (let index = result.length - 1; index > 0; index -= 1) {
    const swapIndex = Math.floor(random() * (index + 1));
    [result[index], result[swapIndex]] = [result[swapIndex], result[index]];
  }
  return result;
};

const pick = (items, random = Math.random) =>
  items[Math.min(items.length - 1, Math.floor(random() * items.length))];

// ---------------------------------------------------------------------------
// Math skills (friendly badges for the results overlay)
// ---------------------------------------------------------------------------

export const MATH_SKILLS = {
  counting: { label: "Counting Power" },
  adding: { label: "Adding Power" },
  comparing: { label: "More or Less" },
  patterns: { label: "Pattern Power" },
  numerals: { label: "Number Power" },
  quantity: { label: "How Many?" },
};

// Templates where a wrong tap costs a mistake (the counting/placing ones cannot
// be "missed"). Used for scoring.
export const MISSABLE_TYPES = new Set(["compareGroups", "colorPattern", "numberHunt", "countAnswer"]);

// ---------------------------------------------------------------------------
// River layout — unit coordinates (x 0..1 across, y 0..1 down)
// ---------------------------------------------------------------------------

// The five spots the stepping-stone path can occupy, arcing across the water.
export const STONE_SLOTS = [
  { x: 0.315, y: 0.52 },
  { x: 0.415, y: 0.468 },
  { x: 0.515, y: 0.505 },
  { x: 0.615, y: 0.455 },
  { x: 0.71, y: 0.495 },
];

// The slots a path of `count` stones uses — spread evenly across the arc so a
// 3-stone path still spans the whole river.
export const pathSlots = (count) => {
  const n = Math.max(1, Math.min(count, STONE_SLOTS.length));
  if (n <= 1) return [STONE_SLOTS[Math.floor(STONE_SLOTS.length / 2)]];
  return Array.from({ length: n }, (_, i) =>
    STONE_SLOTS[Math.round((i * (STONE_SLOTS.length - 1)) / (n - 1))]
  );
};

export const LAYOUT = {
  waitingSpot: { x: 0.135, y: 0.545 },
  entrySpot: { x: -0.16, y: 0.545 },
  arrivalSpot: { x: 0.875, y: 0.475 },
  friendSpots: [
    { x: 0.845, y: 0.335 },
    { x: 0.925, y: 0.285 },
    { x: 0.795, y: 0.25 },
    { x: 0.885, y: 0.195 },
    { x: 0.935, y: 0.385 },
  ],
  riverBand: { start: 0.27, end: 0.76 },
  leftPad: { x: 0.375, y: 0.665 },
  rightPad: { x: 0.645, y: 0.665 },
  countPad: { x: 0.515, y: 0.655 },
};

// Hand-tuned clusters (unit offsets, as a fraction of pad width) for 1–6 items
// on a lily pad — shared by the compare pads and the "how many?" pad.
const PAD_LAYOUTS = [
  [{ x: 0, y: 0 }],
  [{ x: -0.16, y: 0.02 }, { x: 0.16, y: -0.04 }],
  [{ x: -0.18, y: 0.08 }, { x: 0.02, y: -0.14 }, { x: 0.19, y: 0.06 }],
  [{ x: -0.18, y: -0.1 }, { x: 0.16, y: -0.12 }, { x: -0.12, y: 0.14 }, { x: 0.18, y: 0.12 }],
  [{ x: -0.2, y: -0.08 }, { x: 0.14, y: -0.16 }, { x: 0.22, y: 0.1 }, { x: -0.08, y: 0.16 }, { x: 0, y: -0.01 }],
  [{ x: -0.2, y: -0.12 }, { x: 0.06, y: -0.18 }, { x: 0.22, y: -0.04 }, { x: -0.22, y: 0.08 }, { x: 0, y: 0.1 }, { x: 0.18, y: 0.16 }],
];

export const padItemOffset = (i, count) => {
  const points = PAD_LAYOUTS[Math.max(1, Math.min(count, PAD_LAYOUTS.length)) - 1];
  return points[Math.min(i, points.length - 1)];
};

// ---------------------------------------------------------------------------
// Hop styles — every species moves with its own weight and spring
// ---------------------------------------------------------------------------

export const HOP_STYLES = {
  // Turtle: low, slow, deliberate — lands with a thud.
  plodding: { apexLift: 0.045, crouch: 0.16, rise: 0.3, fall: 0.24, rest: 0.22, stretch: 0.6, lean: 4, heavy: true },
  // Bunny: sky-high springs with big stretch.
  springy: { apexLift: 0.088, crouch: 0.09, rise: 0.2, fall: 0.15, rest: 0.1, stretch: 1.35, lean: 9, heavy: false },
  // Duckling: pops up fast, then flutters down slowly.
  fluttery: { apexLift: 0.075, crouch: 0.07, rise: 0.18, fall: 0.28, rest: 0.12, stretch: 1.1, lean: 6, heavy: false },
  // Fox: long, graceful bounds.
  bounding: { apexLift: 0.07, crouch: 0.11, rise: 0.24, fall: 0.17, rest: 0.13, stretch: 1.2, lean: 10, heavy: false },
  // Hedgehog: small, quick, nervous hops.
  scurrying: { apexLift: 0.05, crouch: 0.08, rise: 0.16, fall: 0.13, rest: 0.09, stretch: 0.9, lean: 5, heavy: false },
  // Raccoon: eager, zippy, barely pauses.
  zippy: { apexLift: 0.06, crouch: 0.07, rise: 0.17, fall: 0.13, rest: 0.07, stretch: 1.15, lean: 8, heavy: false },
};

// A softer copy for walking on land (entering, joining the friends).
export const onLandStyle = (style) => ({
  ...style,
  apexLift: style.apexLift * 0.5,
  crouch: style.crouch * 0.6,
  rest: style.rest * 0.5,
});

// ---------------------------------------------------------------------------
// Stone colors (pattern challenge) — each color has its own audible pitch
// ---------------------------------------------------------------------------

export const STONE_COLORS = {
  red: { id: "red", asset: asset("cc_stone_red"), tint: "#F25C52", pitch: 0.15 },
  blue: { id: "blue", asset: asset("cc_stone_blue"), tint: "#408FF5", pitch: 0.45 },
  yellow: { id: "yellow", asset: asset("cc_stone_yellow"), tint: "#FFC22E", pitch: 0.7 },
  green: { id: "green", asset: asset("cc_stone_green"), tint: "#4CC266", pitch: 0.95 },
};

export const PLAIN_STONE = asset("cc_stone");
const COLOR_KEYS = Object.keys(STONE_COLORS);

// ---------------------------------------------------------------------------
// Critters
// ---------------------------------------------------------------------------

// ElevenLabs voice ids — pinned to match PixleyIOS (CritterVoice.swift). The
// narrator handles prompts, counting, and celebrations; each critter speaks
// its own intro/thanks lines. rate/pitch are the on-device fallback profile
// used when the network TTS is unavailable.
export const NARRATOR_VOICE = { id: "NoOVOzCQFLOvtsMoNcdT", rate: 0.95, pitch: 1.05 };

export const CRITTERS = [
  {
    id: "turtle",
    name: "Pebble the Turtle",
    waitingAsset: asset("cc_turtle_waiting"),
    happyAsset: asset("cc_turtle_happy"),
    hopKey: "plodding",
    voice: { id: "NCXuWNJQQvNzPsNoESwl", rate: 0.8, pitch: 0.8 },
    introLines: [
      "Pebble the Turtle is too sleepy to swim today. Build her a path!",
      "Pebble wants a nap on the sunny side of the river. Help her over!",
      "Pebble walks so slowly… stepping stones would be much faster!",
      "The water is too splashy for sleepy Pebble. Can you help her cross?",
    ],
    thanksLines: [
      "Pebble made it across! She's doing a happy wiggle!",
      "Pebble says thank you… and now it's time for her nap.",
      "Slow and steady! Pebble made it all the way across.",
      "Pebble is so happy, she's wiggling her little tail!",
    ],
  },
  {
    id: "bunny",
    name: "Clover the Bunny",
    waitingAsset: asset("cc_bunny_waiting"),
    happyAsset: asset("cc_bunny_happy"),
    hopKey: "springy",
    voice: { id: "XJ2fW4ybq7HouelYYGcL", rate: 1.15, pitch: 1.6 },
    introLines: [
      "Clover the Bunny doesn't want to get her paws wet!",
      "Clover wants to play hopscotch — with real stepping stones!",
      "Clover is ready to hop, hop, hop! She just needs a path.",
      "Clover smells sweet clover across the river. Help her reach it!",
    ],
    thanksLines: [
      "Clover hopped all the way across. Hooray!",
      "Boing, boing, boing! Clover made it!",
      "Clover says that was the best hopping day ever!",
      "Clover's paws stayed dry the whole way. Thank you!",
    ],
  },
  {
    id: "duckling",
    name: "Dot the Duckling",
    waitingAsset: asset("cc_duckling_waiting"),
    happyAsset: asset("cc_duckling_happy"),
    hopKey: "fluttery",
    voice: { id: "M5t0724ORuAGCh3p3DUR", rate: 1.2, pitch: 1.8 },
    introLines: [
      "Dot the Duckling is too little for the big river. Help her over!",
      "Dot's mama is waiting on the other side. Help her get there!",
      "Little Dot is too small to paddle across. Stones would be just right!",
      "Peep peep! Dot the Duckling needs a path across the water.",
    ],
    thanksLines: [
      "Dot made it — what a brave little duckling!",
      "Peep peep hooray! Dot is safe on the other side.",
      "Dot flapped her tiny wings with joy. You did it!",
      "What a big adventure for such a little duck. Thank you!",
    ],
  },
  {
    id: "fox",
    name: "Rusty the Fox",
    waitingAsset: asset("cc_fox_waiting"),
    happyAsset: asset("cc_fox_happy"),
    hopKey: "bounding",
    voice: { id: "fBD19tfE58bkETeiwUoC", rate: 1.05, pitch: 1.0 },
    introLines: [
      "Rusty the Fox wants to visit his friends on the other side.",
      "Rusty is off on an adventure — but the river is in his way!",
      "Rusty can almost see his den from here. Help him hop home!",
      "Rusty the Fox is racing the wind today. Build him a speedy path!",
    ],
    thanksLines: [
      "Rusty is across! His tail is wagging with joy!",
      "Zoom! Rusty made it in record time!",
      "Rusty says you'd make a great adventure buddy!",
      "Rusty did a big leap and an even bigger smile. Thank you!",
    ],
  },
  {
    id: "hedgehog",
    name: "Bramble the Hedgehog",
    waitingAsset: asset("cc_hedgehog_waiting"),
    happyAsset: asset("cc_hedgehog_happy"),
    hopKey: "scurrying",
    voice: { id: "GsfuR3Wo2BACoxELWyEF", rate: 0.95, pitch: 1.2 },
    introLines: [
      "Bramble the Hedgehog is scared of deep water. You can help!",
      "Bramble is a little nervous… but with your help, he can be brave!",
      "Bramble whispers: could you build me a path, please?",
      "Bramble curled into a ball when he saw the river. Show him it's safe!",
    ],
    thanksLines: [
      "Bramble made it, safe and dry. You're a hero!",
      "Bramble was brave the whole way across!",
      "Bramble isn't scared anymore — you helped him feel safe!",
      "A tiny cheer from a tiny hedgehog: thank you, thank you!",
    ],
  },
  {
    id: "raccoon",
    name: "Momo the Raccoon",
    waitingAsset: asset("cc_raccoon_waiting"),
    happyAsset: asset("cc_raccoon_happy"),
    hopKey: "zippy",
    voice: { id: "iukn3a1vSSNFmdi5NZS4", rate: 1.2, pitch: 1.1 },
    introLines: [
      "Momo the Raccoon is late for the berry picnic across the river!",
      "Momo the Raccoon can't wait — there are snacks on the other side!",
      "Quick, quick! Momo has somewhere very important to be!",
      "Momo zoomed all the way here… and forgot how to swim!",
    ],
    thanksLines: [
      "Momo made it to the picnic. Thank you, helper!",
      "Momo did a happy dance with his berry. Hooray!",
      "Made it! Momo says you're the fastest bridge-builder ever!",
      "Momo is off to the picnic — he saved you a berry!",
    ],
  },
];

export const NARRATOR_INTRO =
  "The river is wide and the little critters can't get across! Build stepping-stone paths and help every friend over.";

export const CELEBRATIONS = [
  "You did it! Every critter is safe across the river!",
  "Hip hip hooray! All the critters made it across!",
  "The river is quiet and every friend is home. Wonderful helping!",
];

// The little friends a compare / count group can be made of.
const COMPARE_ITEMS = [
  { asset: asset("cc_frog"), name: "frogs" },
  { asset: asset("cc_butterfly"), name: "butterflies" },
  { asset: asset("cc_berry"), name: "berries" },
  { asset: asset("cc_flower"), name: "flowers" },
];

// ---------------------------------------------------------------------------
// Challenge builders — each returns a fully described, self-contained payload
// ---------------------------------------------------------------------------

export const findNumberLine = (n) => `Find number ${numberWord(n)}!`;

const buildPlaceStones = (count) => ({
  type: "placeStones",
  skill: "counting",
  count,
  rewardStoneCount: null,
  prompt: `Tap the sparkly water to place ${numberWord(count)} stones!`,
  successLine: `${cap(numberWord(count))} stones — the path is ready!`,
  missLine: null,
});

const buildAddOneMore = (existing) => ({
  type: "addOneMore",
  skill: "adding",
  existing,
  total: existing + 1,
  rewardStoneCount: null,
  prompt: `${cap(numberWord(existing))} stones… add one more to make ${numberWord(existing + 1)}!`,
  successLine: `You made ${numberWord(existing + 1)} stones!`,
  missLine: null,
});

const buildCompare = (moreRange, allowFewer, random = Math.random) => {
  const item = pick(COMPARE_ITEMS, random);
  const wantFewer = allowFewer && random() < 0.5;
  let more;
  let fewer;
  if (wantFewer) {
    // The winning (fewer) pad becomes the bridge — keep it 2–3 stones.
    fewer = randomInt(2, 3, random);
    more = randomInt(fewer + 2, 6, random);
  } else {
    // The "more" group is drawn from a level-scaled range (iOS moreRange).
    more = randomInt(moreRange.min, moreRange.max, random);
    // Keep the groups visibly different (a gap of at least 2).
    fewer = randomInt(1, more - 2, random);
  }
  const moreOnLeft = random() < 0.5;
  const leftCount = moreOnLeft ? more : fewer;
  const rightCount = moreOnLeft ? fewer : more;
  const winningCount = wantFewer ? fewer : more;
  return {
    type: "compareGroups",
    skill: "comparing",
    itemAsset: item.asset,
    itemName: item.name,
    leftCount,
    rightCount,
    wantFewer,
    moreCount: more,
    fewerCount: fewer,
    moreIsLeft: moreOnLeft,
    winningCount,
    rewardStoneCount: winningCount,
    prompt: `Tap the lily pad with ${wantFewer ? "FEWER" : "MORE"} ${item.name}!`,
    successLine: wantFewer
      ? `Yes! ${cap(numberWord(fewer))} is fewer than ${numberWord(more)}!`
      : `Yes! ${cap(numberWord(more))} is more than ${numberWord(fewer)}!`,
    missLine: wantFewer
      ? `Hmm — that pad has ${numberWord(more)}. Let's find the pad with fewer!`
      : `Hmm — that pad has ${numberWord(fewer)}. Let's find the pad with more!`,
  };
};

const buildColorPattern = (allowDouble = false, random = Math.random) => {
  const colors = shuffle(COLOR_KEYS, random);
  const a = colors[0];
  const b = colors[1];
  // The bridge is 4 or 5 stones; the child completes the last one. A-B-A-B…
  // normally; at higher levels sometimes A-A-B-B… (doubled) — either way the
  // answer is just the pattern continued.
  const length = randomInt(4, 5, random);
  const doubled = allowDouble && random() < 0.5;
  const pattern = Array.from({ length }, (_, i) =>
    doubled ? (Math.floor(i / 2) % 2 === 0 ? a : b) : (i % 2 === 0 ? a : b)
  );
  const options = shuffle([a, b, colors[2]], random);
  const answer = pattern[pattern.length - 1];
  const shown = pattern.slice(0, -1);
  return {
    type: "colorPattern",
    skill: "patterns",
    pattern,
    options,
    answer,
    shown,
    rewardStoneCount: null,
    prompt: "Which stone comes next? Finish the pattern!",
    successLine: `${cap(answer)}! You finished the pattern!`,
    missLine: `Let's say it together — ${shown.join(", ")}… What comes next?`,
  };
};

const buildNumberHunt = (count) => ({
  type: "numberHunt",
  skill: "numerals",
  count,
  rewardStoneCount: null,
  prompt: "Tap the number stones in order — start at one!",
  successLine: `${cap(numberWord(count))} numbers, all in order. Amazing!`,
  missLine: null,
});

const buildCountAnswer = (range, tight = false, random = Math.random) => {
  const item = pick(COMPARE_ITEMS, random);
  // The counted pad becomes the bridge, so the range is capped at five slots.
  const count = randomInt(range.min, range.max, random);
  const options = new Set([count]);
  if (tight) {
    // Neighbours only — the child must really count, not estimate.
    options.add(Math.max(1, count - 1));
    options.add(Math.min(6, count + 1));
  } else {
    let guard = 0;
    while (options.size < 3 && guard < 30) {
      options.add(randomInt(Math.max(1, count - 2), Math.min(6, count + 2), random));
      guard += 1;
    }
  }
  while (options.size < 3) options.add(count + options.size);
  return {
    type: "countAnswer",
    skill: "quantity",
    itemAsset: item.asset,
    itemName: item.name,
    count,
    options: Array.from(options).sort((x, y) => x - y),
    rewardStoneCount: count,
    prompt: `Count the ${item.name}! How many are on the lily pad?`,
    successLine: `Yes! ${cap(numberWord(count))} ${item.name}!`,
    missLine: "Let's count together, nice and slow!",
  };
};

// Public builders (also used directly by tests). `makeCompare`/`makeCountAnswer`
// keep their simple legacy signatures (a max/min number) by wrapping the
// range-based internal builders.
export const makeCompare = (maxCount = 5, allowFewer = false, random = Math.random) =>
  buildCompare({ min: 4, max: maxCount }, allowFewer, random);
export const makeCountAnswer = (minCount = 3, random = Math.random) =>
  buildCountAnswer({ min: minCount, max: 5 }, false, random);
export const makeColorPattern = (allowDouble = false, random = Math.random) =>
  buildColorPattern(allowDouble, random);
export const makeNumberHunt = (count) => buildNumberHunt(count);
export const makePlaceStones = (count) => buildPlaceStones(count);
export const makeAddOneMore = (existing) => buildAddOneMore(existing);

// ---------------------------------------------------------------------------
// Adaptive difficulty (1 gentle → 3 challenging)
// ---------------------------------------------------------------------------

export const MIN_LEVEL = 1;
export const MAX_LEVEL = 3;
const clampLevel = (level) => Math.max(MIN_LEVEL, Math.min(Math.round(level) || 2, MAX_LEVEL));

// Friendly names shown on the result overlay when the level changes — mirrors
// the iOS tuning names (Gentle / Just Right / Challenge).
export const LEVEL_NAMES = { 1: "Gentle", 2: "Just Right", 3: "Challenge" };
export const levelName = (level) => LEVEL_NAMES[clampLevel(level)] || "Just Right";

const LEVEL_KEY = "cc_level";

// Cached locally for instant starts (there is no per-profile difficulty
// endpoint on the web backend, so this is localStorage only — matching the
// plan's "if none, localStorage only" guidance).
export const loadLevel = () => {
  try {
    const raw = parseInt(localStorage.getItem(LEVEL_KEY), 10);
    if (Number.isFinite(raw)) return clampLevel(raw);
  } catch {
    // No storage — fall through to the gentle-middle default.
  }
  return 2;
};

export const saveLevel = (level) => {
  try {
    localStorage.setItem(LEVEL_KEY, String(clampLevel(level)));
  } catch {
    // The game still works without a persisted level.
  }
};

// iOS rating: a clean round scores 1.0; each miss knocks off a quarter.
export const ratingForMistakes = (mistakes) => Math.max(0, 1 - Math.max(0, mistakes) * 0.25);

// Two clean rounds in a row step the level up; two rough ones step it down.
// Returns the next { level, ratings } — apply to the NEXT round.
export const advanceLevel = (level, ratings, mistakes) => {
  const current = clampLevel(level);
  let nextRatings = [...ratings, ratingForMistakes(mistakes)].slice(-3);
  let nextLevel = current;
  const lastTwo = nextRatings.slice(-2);
  if (lastTwo.length === 2) {
    if (lastTwo.every((r) => r >= 0.99) && current < MAX_LEVEL) {
      nextLevel = current + 1;
      nextRatings = [];
    } else if (lastTwo.every((r) => r <= 0.5) && current > MIN_LEVEL) {
      nextLevel = current - 1;
      nextRatings = [];
    }
  }
  return { level: nextLevel, ratings: nextRatings };
};

// ---------------------------------------------------------------------------
// Per-round mood wash (a whisper of colored light so back-to-back rounds feel
// like different times of day) — mirrors iOS CritterSceneMood.
// ---------------------------------------------------------------------------

export const MOODS = ["morning", "noon", "golden"];
export const randomMood = (random = Math.random) => MOODS[Math.floor(random() * MOODS.length)];

// The fifth crossing is a bigger version of a random template — this is also
// where the trickier "fewer" comparison can appear. Level 1 keeps the finale
// gentle; levels 2–3 open up the full spread.
const buildFinale = (level, random = Math.random) => {
  if (level === 1) {
    switch (randomInt(0, 2, random)) {
      case 0:
        return buildPlaceStones(randomInt(3, 4, random));
      case 1:
        return buildAddOneMore(randomInt(2, 3, random));
      default:
        return buildCountAnswer({ min: 3, max: 4 }, false, random);
    }
  }
  switch (randomInt(0, 4, random)) {
    case 0:
      return buildPlaceStones(randomInt(4, 5, random));
    case 1:
      return buildCompare({ min: 4, max: 5 }, true, random);
    case 2:
      return buildNumberHunt(randomInt(4, 5, random));
    case 3:
      return buildCountAnswer({ min: 4, max: 5 }, level >= 3, random);
    default:
      return buildAddOneMore(randomInt(3, 4, random));
  }
};

// ---------------------------------------------------------------------------
// Round factory — five crossings: a warm-up, three middle templates, a finale.
// Every count is drawn from a level-sized RANGE (1 gentle → 3 challenging), so
// no challenge — and no bridge — is ever the same width two rounds in a row.
// ---------------------------------------------------------------------------

export const makeRound = (level = 2, random = Math.random) => {
  const lvl = clampLevel(level);
  const critters = shuffle(CRITTERS, random);

  const warmupRange = lvl === 1 ? { min: 2, max: 3 } : lvl === 3 ? { min: 4, max: 5 } : { min: 3, max: 4 };
  const addRange = lvl === 3 ? { min: 3, max: 4 } : { min: 2, max: 3 };
  const moreRange = lvl === 1 ? { min: 3, max: 4 } : { min: 4, max: 5 };
  const huntRange = lvl === 1 ? { min: 2, max: 3 } : lvl === 3 ? { min: 4, max: 5 } : { min: 3, max: 4 };
  const countRange = lvl === 1 ? { min: 2, max: 3 } : lvl === 3 ? { min: 4, max: 5 } : { min: 3, max: 5 };

  const middlePool = shuffle(
    [
      buildCompare(moreRange, lvl >= 3, random),
      buildAddOneMore(randomInt(addRange.min, addRange.max, random)),
      buildColorPattern(lvl >= 3, random),
      buildNumberHunt(randomInt(huntRange.min, huntRange.max, random)),
      buildCountAnswer(countRange, lvl >= 3, random),
    ],
    random
  ).slice(0, 3);

  const warmup = buildPlaceStones(randomInt(warmupRange.min, warmupRange.max, random));
  const challenges = [warmup, ...middlePool, buildFinale(lvl, random)];

  return challenges.map((challenge, index) => {
    const critter = critters[index % critters.length];
    return {
      id: `${index}-${critter.id}`,
      critter,
      challenge,
      introLine: pick(critter.introLines, random),
      thanksLine: pick(critter.thanksLines, random),
    };
  });
};

// ---------------------------------------------------------------------------
// Scoring — gentle: misses only nudge the number, never below 40, no lose state
// ---------------------------------------------------------------------------

export const scoreForMistakes = (mistakes) => Math.max(40, 100 - Math.max(0, mistakes) * 10);

// The distinct skills practiced across a round (for the results badges).
export const skillsInRound = (tasks) => {
  const seen = [];
  tasks.forEach((task) => {
    if (!seen.includes(task.challenge.skill)) seen.push(task.challenge.skill);
  });
  return seen;
};

export const celebrationLine = (random = Math.random) => pick(CELEBRATIONS, random);

// ---------------------------------------------------------------------------
// Voice prefetch — every line a challenge might say, in play order, so the
// round's TTS cache can be warmed up front (mirrors iOS allSpokenLines).
// ---------------------------------------------------------------------------

export const challengeSpokenLines = (challenge) => {
  const lines = [challenge.prompt, challenge.successLine];
  if (challenge.missLine) lines.push(challenge.missLine);
  switch (challenge.type) {
    case "placeStones":
      for (let n = 1; n <= challenge.count; n += 1) lines.push(numberWord(n));
      break;
    case "addOneMore":
      for (let n = 1; n <= challenge.total; n += 1) lines.push(numberWord(n));
      break;
    case "colorPattern":
      challenge.shown.forEach((color) => lines.push(color));
      break;
    case "numberHunt":
      for (let n = 1; n <= challenge.count; n += 1) {
        lines.push(numberWord(n));
        lines.push(findNumberLine(n));
      }
      break;
    case "countAnswer":
      for (let n = 1; n <= challenge.count; n += 1) lines.push(numberWord(n));
      break;
    default:
      break;
  }
  return lines;
};
